/*
 * Copyright (c) Nexign, JSC, 2024-2025.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Nikolay Sviridov
 * @version 1.29.0
 * @since 1.29.0
 */

import { Fragment, useCallback } from 'react';

import { connect, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardAdditionalReports,
} from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { IWizardAdditionalReportsTranslation } from '@modules/internationalization/types/wizards/additionalReports';
import { EWizardName, WIZARD_ALL_ENTITIES } from '@modules/wizard/wizardConstants';
import { IWizardState } from '@modules/wizard/wizardReducer';
import { makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';
import { IRootReducer } from '@reducers/rootReducer';
import { IRouteParams } from '@routes/constants/routesConstants';

import Title from '../../../components/common/Title/Title';
import withAsyncStateManagement from '../../../modules/asyncStateManagment/WithAsyncStateManagement';
import PageLocale from '../../../modules/internationalization/components/PageLocale';
import Wizard, { IWizardProps } from '../../../modules/wizard/components/Wizard';
import { wizardAdditionalReportsHandleNextAction } from '../wizardAdditionalReportsActions';
import { EWizardAdditionalReportsSteps } from '../wizardAdditionalReportsConsant';
import wizardAdditionalReportsReducer, { IWizardAdditionalReportsReducer } from '../wizardAdditionalReportsReducer';
import {
    makeSelectWizardAdditionalReportsIsStepValid,
    makeSelectWizardAdditionalReportsParam,
} from '../wizardAdditionalReportsSelectors';
import WizardBillsDocStepChoose from './WizardAdditionalReportsStepChoose';
import WizardBillsDocStepConfirmation from './WizardAdditionalReportsStepConfirmation';
import WizardBillsDocStepPrepare from './WizardAdditionalReportsStepPrepare';

const WizardAdditionalReports = ({
                                     basketType,
                                     error,
                                     isStepValid,
                                     stepName,
                                     translationAdditionalReports,
                                     handleNext,
                                 }: IConnectedProps & IDispatchProps & Pick<IWizardProps, 'basketType'>) => {

    const { clientId } = useParams<IRouteParams>();

    const translationCommon = useSelector(makeSelectTranslationCommon());
    const { instructionsLinks: { repsAdd } } = translationCommon;

    const renderStep = useCallback(() => {
        switch (stepName) {
            case EWizardAdditionalReportsSteps.ChooseStep:
                return <WizardBillsDocStepChoose />;
            case EWizardAdditionalReportsSteps.PrepareStep:
                return <WizardBillsDocStepPrepare basketType={basketType}/>;
            case EWizardAdditionalReportsSteps.ConfirmStep:
                return <WizardBillsDocStepConfirmation />;
        }
    }, [basketType, stepName]);

    return (
        <Fragment>
            <PageLocale lazy
                        fileNames={[
                            TranslationFileNamesEnum.wizardAdditionalReports,
                            TranslationFileNamesEnum.wizard,
                        ]} />

            <Title pageName={ECommonTranslationPages.wizardAdditionalReports} />

            <Wizard
                basketType={basketType}
                entityId={clientId ? parseInt(clientId) : WIZARD_ALL_ENTITIES}
                instructionLink={repsAdd}
                isNextDisabled={!isStepValid}
                name={EWizardName.ADDITIONAL_REPORTS}
                translationProps={{
                    error: translationAdditionalReports.error,
                    steps: translationAdditionalReports.steps,
                    title: translationAdditionalReports.title,
                }}
                validateError={error}
                onWizardNextHandler={handleNext}
            >
                {renderStep()}
            </Wizard>
        </Fragment>
    );
};

export interface IConnectedProps extends Pick<IWizardState, 'stepName'>,
    Pick<IWizardAdditionalReportsReducer, 'isStepValid' | 'error'> {
    translationAdditionalReports: IWizardAdditionalReportsTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    error: makeSelectWizardAdditionalReportsParam('error'),
    isStepValid: makeSelectWizardAdditionalReportsIsStepValid(),
    stepName: makeSelectWizardStepName(),
    translationAdditionalReports: makeSelectTranslationWizardAdditionalReports(),
});

export interface IDispatchProps {
    handleNext: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    handleNext: () => dispatch(wizardAdditionalReportsHandleNextAction()),
});

export default withAsyncStateManagement(
    connect(mapStateToProps, mapDispatchToProps)(WizardAdditionalReports),
    { reducer: { wizardAdditionalReports: wizardAdditionalReportsReducer } },
);
